import {
  ChangeEvent,
  FunctionComponent,
  InputHTMLAttributes,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Noop } from 'react-hook-form';
import StringUtils from '../../utils';

/**
 * FormFile props
 */
export interface FormFileProps {
  value?: FileList | string;
  onChange: (files: FileList) => void;
  onBlur: Noop;
  buttonLabel?: string;
  fileProps?: InputHTMLAttributes<HTMLInputElement>;
  progress?: number;
  disabled?: boolean;
}

/**
 * FormFile Component
 */
const FormFile: FunctionComponent<FormFileProps> = ({
  value,
  onChange,
  onBlur,
  buttonLabel,
  fileProps,
  progress,
  disabled,
}) => {
  const [key] = useState(StringUtils.generateKey());

  const preview = useMemo<string>(() => {
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object' && value.length > 0) {
      return [...value].map((file: File) => file.name).join(', ');
    }
    return 'Select file';
  }, [value]);

  const isEmpty = useMemo(() => preview === 'Select file', [preview]);

  const fieldOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files as FileList;
      onChange(files || []);
    },
    [onChange],
  );

  return (
    <label htmlFor={key}>
      <div
        className="border p-2 flex flex-row rounded items-center gap-2 bg-white"
        style={{
          backgroundColor: disabled ? 'rgba(0, 0, 0, 0.04)' : undefined,
        }}>
        <div
          className={`bg-gray-200 p-1 px-2 rounded ${
            !disabled ? 'hover:bg-gray-300 cursor-pointer' : ''
          }`}>
          <span
            className="text-blue text-xs font-semibold"
            style={{ whiteSpace: 'nowrap' }}>
            {buttonLabel || (isEmpty ? 'Upload file' : 'Replace file')}
          </span>
        </div>
        <span
          className={
            !isEmpty ? 'text-black truncate max-w-[400px]' : 'text-gray'
          }>
          {preview}
        </span>
      </div>
      {progress && progress > 0 ? (
        <div
          className="h-[5px] bg-blue transition-all"
          style={{ width: `${progress}%` }}
        />
      ) : null}
      <input
        disabled={disabled}
        name={key}
        onChange={fieldOnChange}
        id={key}
        type="file"
        className="hidden"
        style={{ display: 'none' }}
        onBlur={onBlur}
        {...fileProps}
      />
    </label>
  );
};

FormFile.defaultProps = {
  value: undefined,
  buttonLabel: 'Upload file',
};

export default FormFile;
